import React from "react";
import PropTypes from "prop-types";
import { noop } from "utils/lodash";
import cx from "classnames";
import { List } from "immutable";
import ImmutablePropTypes from "react-immutable-proptypes";
import DropdownButton from "../../DropdownButton";
import { isString, isStringFilled } from "../../../utils";

import { selectData } from "../../../utils/reducerUtils";

import {
  VOICE_STARTED,
  VOICE_ENDED,
  VOICE_SPEAKING,
  VOICE_WAITING,
  VOICE_FORCE_END,
} from "../../../constants/postMessage";
import PlainInput from "../PlainInput";
import SendButton from "../SendButton";
import styles from "./chat-input.cssmodule.scss";

const EmojiButton = React.lazy(() => import("../../EmojiButton"));

const initialState = {
  value: "",
  isListening: false,
  sendMessage: false,
  isWaiting: false,
};

const Input = ({
  persistentMenu,
  onSend,
  persistentCallback,
  onClickInput,
  buttonColour,
  readOnly,
  disableEmojiButton,
  isInputDisabled,
  isFocus,
  isBackgroundTransparent,
  fontSize,
  alternativeTexts,
  voiceEnabled,
  autoSend,
  isSpeaking,
  inputScriptType,
  inputScriptStyles,
}) => {
  const [dataInput, setDataInput] = React.useState(initialState);
  const [openEmoji, setOpenEmoji] = React.useState(false);
  const { value, isListening, sendMessage, isWaiting } = dataInput;

  const receiveMessage = React.useCallback(e => {
    const { data } = e;
    switch (data.actionName) {
      case VOICE_STARTED: {
        setDataInput(prevState => ({
          ...prevState,
          isListening: true,
          isWaiting: false,
        }));
        break;
      }
      case VOICE_ENDED: {
        setDataInput(prevState => ({
          ...prevState,
          isListening: false,
          sendMessage: true,
        }));
        break;
      }
      case VOICE_SPEAKING: {
        setDataInput(prevState => ({
          ...prevState,
          value: data.message,
        }));
        break;
      }
      case VOICE_WAITING: {
        setDataInput(prevState => ({
          ...prevState,
          isListening: false,
          sendMessage: true,
          isWaiting: true,
        }));
        break;
      }
      case VOICE_FORCE_END: {
        setDataInput(prevState => ({
          ...prevState,
          isListening: false,
          isWaiting: false,
        }));
        break;
      }
      default: {
        break;
      }
    }
  }, []);

  React.useEffect(() => {
    if (voiceEnabled) {
      window.addEventListener("message", receiveMessage);
    }
    return () => {
      window.removeEventListener("message", receiveMessage);
    };
  }, []);

  React.useEffect(() => {
    if (autoSend && sendMessage) {
      onSend(value);
      setDataInput(prevState => {
        if (prevState.isWaiting) {
          return {
            ...prevState,
            sendMessage: false,
            value: "",
          };
        }
        return initialState;
      });
    }
  }, [sendMessage]);

  const onChange = React.useCallback(dataValue => {
    setDataInput(prevState => ({ ...prevState, value: dataValue }));
  }, []);

  const handleOnSend = React.useCallback(
    (valueInInput = false) => {
      if (!isInputDisabled) {
        if (typeof valueInInput === "string") {
          onSend(valueInInput);
        } else {
          onSend(value);
          setDataInput(prevState => ({ ...prevState, value: "" }));
        }
      }
    },
    [value, isInputDisabled]
  );

  const insertText = React.useCallback(text => {
    setDataInput(prevState => ({
      ...prevState,
      value: prevState.value + text,
    }));
  }, []);

  const onSelectEmoji = React.useCallback(
    emoji => insertText(emoji.native),
    []
  );

  const handleOnClickInput = React.useCallback(() => {
    if (isInputDisabled) {
      onClickInput();
    }
  }, [isInputDisabled, onClickInput]);

  const handleListener = React.useCallback(() => {
    if (isListening || isWaiting) {
      window.top.postMessage(
        {
          message: "end-force-micro",
        },
        "*"
      );
      return;
    }
    window.top.postMessage(
      {
        message: "start-force-micro",
      },
      "*"
    );
    window.top.postMessage(
      {
        message: "listen-micro",
        startToListen: true,
      },
      "*"
    );
  }, [isListening, isWaiting]);

  const onClickHandler = React.useCallback(() => {
    if (disableEmojiButton || isInputDisabled) return;

    setOpenEmoji(state => !state);
  }, [disableEmojiButton, isInputDisabled]);

  const handleKeyPress = React.useCallback(
    e => {
      if (e.key === "Enter") {
        onClickHandler();
      }
    },
    [onClickHandler]
  );

  // const handleClickOutside = React.useCallback(() => {
  //   setOpenEmoji(false);
  // }, []);

  const isPersistentMenu = React.useMemo(
    () => persistentMenu.size > 0,
    [persistentMenu.size]
  );

  const persistentMenuAltText =
    alternativeTexts &&
    isString(selectData(alternativeTexts, "persistent_menu"))
      ? selectData(alternativeTexts, "persistent_menu")
      : "Extra options";

  const inputFieldAltText =
    alternativeTexts && isString(selectData(alternativeTexts, "input_field"))
      ? selectData(alternativeTexts, "input_field")
      : "Text input field";
  const customPlaceholder =
    alternativeTexts &&
    isStringFilled(selectData(alternativeTexts, "input_placeholder"))
      ? selectData(alternativeTexts, "input_placeholder")
      : "Write here...";

  const addEmojiButtonAltText =
    alternativeTexts &&
    isString(selectData(alternativeTexts, "add_emoji_button"))
      ? selectData(alternativeTexts, "add_emoji_button")
      : "Add emoji";

  const sendButtonAltText =
    alternativeTexts && isString(selectData(alternativeTexts, "send_button"))
      ? selectData(alternativeTexts, "send_button")
      : "Send message";

  const dropdownContainer = !isBackgroundTransparent
    ? styles.ndDropdownContainer
    : styles.ndDropdownContainerTransparent;

  return (
    <div
      className={cx(styles.ndContainer, {
        [styles.containerInputBottom]: inputScriptType === "bottom",
        [styles.inputContainerDisabled]: isInputDisabled,
      })}
    >
      <div
        className={cx(dropdownContainer, {
          [styles.dropdownContainerInputBottom]: inputScriptType === "bottom",
        })}
      >
        {isPersistentMenu && (
          <DropdownButton
            altText={persistentMenuAltText}
            menu={persistentMenu}
            itemClick={persistentCallback}
            colour={buttonColour}
            disabled={isInputDisabled}
          />
        )}
      </div>
      <>
        <div
          className={cx(styles.ndMessageContainer, {
            [styles.messageContainerInputBottom]: inputScriptType === "bottom",
            [styles.messageContainerWithPersistentMenuInputBottom]:
              isPersistentMenu && inputScriptType === "bottom",
          })}
          style={{
            fontSize,
            ...(inputScriptStyles && inputScriptStyles.toJS()),
          }}
        >
          <div
            className={
              !isBackgroundTransparent ||
              (isBackgroundTransparent && !isPersistentMenu)
                ? styles.input
                : styles.inputTransparent
            }
            onClick={handleOnClickInput}
            data-test="chat-input"
          >
            <PlainInput
              altText={inputFieldAltText}
              customPlaceholder={customPlaceholder}
              onEnter={handleOnSend}
              {...{
                value,
                onChange,
                fontSize,
                readOnly,
                isFocus,
                inputScriptType,
              }}
            />
          </div>
          <div
            className={cx(styles.ndActions, {
              [styles.actionsBottomInput]: inputScriptType === "bottom",
              [styles.actionsWithVoiceBottomInput]:
                voiceEnabled && inputScriptType === "bottom",
            })}
            style={{ cursor: isInputDisabled ? "default" : "pointer" }}
          >
            <div>
              {openEmoji ? (
                <EmojiButton
                  onSelect={onSelectEmoji}
                  autoFocus={false}
                  style={{ position: "absolute", top: -430, right: 0 }}
                />
              ) : null}
              <div
                className={styles.emojiButtonContainer}
                onClick={onClickHandler}
                onKeyPress={handleKeyPress}
                role="button"
                tabIndex={0}
                aria-label={addEmojiButtonAltText}
                title={addEmojiButtonAltText}
              >
                <i
                  className="far fa-smile"
                  style={{
                    fontSize: inputScriptType === "bottom" ? "30px" : "14px",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
        <div
          className={cx(styles.ndSendContainer, {
            [styles.sendContainerInputBottom]: inputScriptType === "bottom",
            [styles.sendContainerInputBottomWithVoice]:
              inputScriptType === "bottom" && voiceEnabled,
          })}
        >
          <SendButton
            colour={buttonColour}
            onClick={handleOnSend}
            altText={sendButtonAltText}
            disabled={isInputDisabled}
          />
        </div>
      </>
      {voiceEnabled && (
        <div
          className={cx(styles.ndMicro, {
            [styles.microContainerInputBottom]: inputScriptType === "bottom",
          })}
        >
          <button
            className={styles.microBox}
            disabled={isSpeaking}
            onClick={handleListener}
            title="Microphone"
            aria-label="Microphone"
            style={{
              opacity: isListening || isWaiting ? 0.7 : 1,
              cursor: isListening || isWaiting ? "default" : "pointer",
              backgroundColor: buttonColour,
            }}
            type="button"
          >
            {!isWaiting && isListening && (
              <div className={styles.boxContainer}>
                <div className={cx(styles.box, styles.box1)} />
                <div className={cx(styles.box, styles.box2)} />
                <div className={cx(styles.box, styles.box3)} />
                <div className={cx(styles.box, styles.box4)} />
              </div>
            )}
            {!isWaiting && !isListening && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="26"
                height="26"
                viewBox="0 0 24 24"
                title="microphone"
              >
                <path d="M12 14c1.66 0 2.99-1.34 2.99-3L15 5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" />
              </svg>
            )}
            {isWaiting && !isListening && (
              <div className={styles.indicator}>
                <span />
                <span />
                <span />
              </div>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

Input.propTypes = {
  persistentMenu: ImmutablePropTypes.list,
  alternativeTexts: ImmutablePropTypes.map,
  persistentCallback: PropTypes.func,
  onClickInput: PropTypes.func,
  onSend: PropTypes.func,
  buttonColour: PropTypes.string,
  readOnly: PropTypes.bool,
  disableEmojiButton: PropTypes.bool,
  isInputDisabled: PropTypes.bool,
  isFocus: PropTypes.bool,
  isBackgroundTransparent: PropTypes.bool,
  fontSize: PropTypes.string,
  voiceEnabled: PropTypes.bool,
  autoSend: PropTypes.bool,
  isSpeaking: PropTypes.bool,
  inputScriptType: PropTypes.string,
  inputScriptStyles: ImmutablePropTypes.map,
};

Input.defaultProps = {
  persistentMenu: List([]),
  onSend: () =>
    console.log("No send function was set in shared/ChatInput instance"), // eslint-disable-line no-console
  persistentCallback: () => console.log("persistentCallback"), // eslint-disable-line no-console
  onClickInput: noop,
  buttonColour: "",
  readOnly: false,
  disableEmojiButton: false,
  isInputDisabled: false,
  isFocus: false,
  voiceEnabled: false,
  isSpeaking: false,
};

export default Input;
